import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Chip, IconButton, Paper, Stack } from '@mui/material';
import { flatMap, isArray, map } from 'es-toolkit/compat';
import React, { useMemo } from 'react';

import { dispatchCopilotPromptInsert } from 'components/Copilot/drawer/promptInsertion';
import { useCopilotDrawerState } from 'components/Copilot/hooks/useCopilotDrawerState';
import { PlotlyFigure, SectionChart } from 'interfaces/agentSession';
import { useParsedBrackets } from 'utils/features/getFeaturesKeysObjectsWebWorker/useParsedBrackets';
import {
  recursiveGetBracketsFromObject,
  recursiveReplaceFeaturesSyntaxWithDisplayNames,
} from 'utils/formatBrackets/replaceBracketsWithDisplayName';
import { useJobResultAugmentation } from 'utils/jobResultAugmentation';
import { useCurrentLabId } from 'utils/useCurrentLab';
import ChartShareMenu from './ChartShareMenu';
import JobResultSlideLabelModeSelect from './JobResultSlideLabelModeSelect';
import StudyReportCardHeader from './StudyReportCardHeader';
import { getStudyReportChartHeight } from './studyReportChartLayout';
import StudyReportDescriptionMarkdown from './StudyReportDescriptionMarkdown';
import ThemedPlotlyFigure from './ThemedPlotlyFigure';

interface Props {
  chart: SectionChart;
  studyId?: string;
  sectionNumber?: number;
  onRemove: () => void;
}

const UNDERSCORE_REGEX = /_/g;
const UNKNOWN_ORCHESTRATION_ID = '';

interface PlotlyPointLinkConfig {
  type?: string;
  customdataIndex?: number;
}

interface PlotlyClickPoint {
  customdata?: unknown;
}

interface PlotlyClickEvent {
  points?: PlotlyClickPoint[];
}

const getPointLinkConfig = (figure: PlotlyFigure): PlotlyPointLinkConfig | null => {
  const layout = (figure.layout as Record<string, unknown>) || {};
  const meta = (layout.meta as Record<string, unknown>) || {};
  const pointLink = meta.pointLink;
  if (!pointLink || typeof pointLink !== 'object') {
    return null;
  }

  return pointLink as PlotlyPointLinkConfig;
};

const extractSlideIdFromPointClick = (event: unknown, linkConfig: PlotlyPointLinkConfig | null): string | null => {
  if (!linkConfig || linkConfig.type !== 'study_slide') {
    return null;
  }

  const firstPoint = (event as PlotlyClickEvent | undefined)?.points?.[0];
  if (!firstPoint) {
    return null;
  }

  const customdata = firstPoint.customdata;
  const customdataIndex = typeof linkConfig.customdataIndex === 'number' ? linkConfig.customdataIndex : 0;

  if (typeof customdata === 'string') {
    return customdata.trim() || null;
  }

  if (!Array.isArray(customdata)) {
    return null;
  }

  const slideId = customdata[customdataIndex];
  if (typeof slideId !== 'string') {
    return null;
  }

  const normalizedSlideId = slideId.trim();
  return normalizedSlideId || null;
};

const buildStudySlideUrl = ({ slideId, studyId, labId }: { slideId: string; studyId?: string; labId?: string }) => {
  const pathname = studyId ? `/study/${studyId}/slides/${slideId}` : `/slides/${slideId}`;
  const searchParams = new URLSearchParams();
  if (labId) {
    searchParams.set('labId', labId);
  }
  const search = searchParams.toString();

  return search ? `${pathname}?${search}` : pathname;
};

// Visual wrapper for one chart inside a report section.
const StudyReportSectionChartCard: React.FC<Props> = ({ chart, studyId, sectionNumber, onRemove }) => {
  // Keep a reference to the mounted plot element for share/download actions.
  const [chartEl, setChartEl] = React.useState<HTMLElement | null>(null);
  const { setCopilotDrawerOpen, setCopilotDrawerTab } = useCopilotDrawerState();
  const { labId } = useCurrentLabId();
  // Use chart-type-aware sizing to keep cards balanced across section layouts.
  const chartHeight = getStudyReportChartHeight(chart.chartType);

  const bracketsToParse = useMemo(
    () =>
      flatMap(
        [...(isArray(chart.plotlyFigure.data) ? chart.plotlyFigure.data : []), chart.plotlyFigure.layout],
        recursiveGetBracketsFromObject
      ),
    [chart.plotlyFigure.data, chart.plotlyFigure.layout]
  );

  const { data: parsedBrackets } = useParsedBrackets({
    bracketsToParse,
    addStainToFeatureName: true,
    studyId: studyId || '',
    orchestrationId: UNKNOWN_ORCHESTRATION_ID,
  });

  const resolvedFigure: PlotlyFigure = useMemo(() => {
    if (!parsedBrackets) {
      return chart.plotlyFigure;
    }

    return {
      ...chart.plotlyFigure,
      data: map(chart.plotlyFigure.data, (dataEntry) =>
        recursiveReplaceFeaturesSyntaxWithDisplayNames(dataEntry, parsedBrackets)
      ) as Record<string, unknown>[],
      layout: recursiveReplaceFeaturesSyntaxWithDisplayNames(chart.plotlyFigure.layout, parsedBrackets) as Record<
        string,
        unknown
      >,
    };
  }, [chart.plotlyFigure, parsedBrackets]);

  const {
    augmentedFigure,
    augmentedDescription,
    showSlideLabelControl,
    slideLabelMode,
    slideLabelOptions,
    setSlideLabelMode,
  } = useJobResultAugmentation({
    studyId,
    sourceConfig: chart.sourceConfig,
    figure: resolvedFigure,
    description: chart.description,
  });
  const displayDescription = augmentedDescription ?? chart.description;

  const displayFigure: PlotlyFigure = useMemo(() => {
    const layout = ((augmentedFigure || resolvedFigure).layout as Record<string, unknown>) || {};
    const layoutWithoutTitle = { ...layout };
    delete layoutWithoutTitle.title;

    return {
      ...(augmentedFigure || resolvedFigure),
      layout: layoutWithoutTitle as Record<string, unknown>,
    };
  }, [augmentedFigure, resolvedFigure]);

  const pointLinkConfig = useMemo(() => getPointLinkConfig(displayFigure), [displayFigure]);

  const handlePointClick = React.useCallback(
    (event: unknown) => {
      const slideId = extractSlideIdFromPointClick(event, pointLinkConfig);
      if (!slideId) {
        return;
      }

      const slideUrl = buildStudySlideUrl({ slideId, studyId, labId: labId || undefined });
      window.open(slideUrl, '_blank', 'noopener,noreferrer');
    },
    [labId, pointLinkConfig, studyId]
  );

  const handleExplainChart = React.useCallback(() => {
    if (!sectionNumber) {
      return;
    }

    setCopilotDrawerOpen(true, 'replaceIn');
    setCopilotDrawerTab('chat', 'replaceIn');
    dispatchCopilotPromptInsert({
      insertText: `the chart "${chart.title}" from section ${sectionNumber} `,
      emptyPromptText: `Explain the chart "${chart.title}" from section ${sectionNumber}.`,
      focus: true,
    });
  }, [chart.title, sectionNumber, setCopilotDrawerOpen, setCopilotDrawerTab]);

  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0, overflow: 'hidden' }}>
      <Stack spacing={1} sx={{ minWidth: 0 }}>
        <StudyReportCardHeader
          title={chart.title}
          description={
            displayDescription ? (
              <StudyReportDescriptionMarkdown content={displayDescription} studyId={studyId} />
            ) : null
          }
          actions={
            <>
              <ChartShareMenu
                chartEl={chartEl}
                figure={displayFigure}
                title={chart.title}
                description={displayDescription}
              />
              <IconButton size="small" onClick={onRemove}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        />

        <Box sx={{ position: 'relative', minWidth: 0 }}>
          {showSlideLabelControl ? (
            <Box sx={{ mb: 1.25 }}>
              <JobResultSlideLabelModeSelect
                value={slideLabelMode}
                options={slideLabelOptions}
                onChange={setSlideLabelMode}
              />
            </Box>
          ) : null}
          <ThemedPlotlyFigure
            figure={displayFigure}
            height={chartHeight}
            preservePlotAreaHeight
            onPlotlyInit={setChartEl}
            onPointClick={pointLinkConfig ? handlePointClick : undefined}
            animateOnUpdate
          />
          {sectionNumber ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<AutoAwesomeRoundedIcon fontSize="small" />}
              onClick={handleExplainChart}
              sx={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                zIndex: 1,
                borderRadius: 999,
                px: 1.25,
                maxWidth: 'calc(100% - 24px)',
                textTransform: 'none',
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'background.paper',
                  borderColor: 'text.primary',
                  boxShadow: 3,
                },
              }}
            >
              Explain this chart
            </Button>
          ) : null}
        </Box>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip label={chart.chartType} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
          <Chip label={chart.sourceType.replace(UNDERSCORE_REGEX, ' ')} size="small" variant="outlined" />
        </Stack>
      </Stack>
    </Paper>
  );
};

export default StudyReportSectionChartCard;
